import { matchSorter } from 'match-sorter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

import analytics from '../../services/analytics';
import { ISymbolsCategory, ISymbol } from '../../data/types';
import useStore from '../../store/store';
import Analytics from '../../services/analytics';

function getName(category: string) {
  return category === 'index' ? 'main' : category;
}

function useApp(symbols: ISymbolsCategory[]) {
  const cachedSearchTerm = useStore((state) => state.searchTerm);
  const [searchTerm, setSearchTerm] = useState(() => cachedSearchTerm || '');
  const [searchFound, setSearchFound] = useState(null);

  const [currentSymbols, setCurrentSymbols] = useState<ISymbolsCategory[]>(() => [...symbols]);
  const debouncedSearchTerm = useDebounce(searchTerm, 0);

  const toggleModal = useStore((state) => state.toggleModal);
  const showModal = useStore((state) => state.showModal);
  const chooseSymbol = useStore((state) => state.addToEditor);
  const setCopyCardExpanded = useStore((state) => state.setEditorExpanded);
  const setSearchText = useStore((state) => state.setSearchTerm);

  const onChangeSearchText = useCallback(
    (searchText: string) => {
      setSearchTerm(searchText);
      setSearchText(searchText);
      analytics.textBoxType();
    },
    [setSearchText],
  );

  const onChangeCategory = useCallback((category, _, page) => {
    analytics.sendGaEvent('category_select', {
      category_name: getName(category),
      page_name: getName(page),
    });
  }, []);

  const onSearchInAllCategories = useCallback(() => {
    // TODO - https://picsart.atlassian.net/browse/ST-1074
  }, []);

  const onToggleFilterModal = useCallback(() => {
    toggleModal();
  }, [toggleModal]);

  const onChooseSymbol = useCallback(
    (symbol: ISymbol) => {
      chooseSymbol(symbol.value);
      setCopyCardExpanded(true);
      Analytics.symbolCopy(symbol.group, symbol.name);
    },
    [chooseSymbol, setCopyCardExpanded],
  );

  useEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setCurrentSymbols(() => {
      if (debouncedSearchTerm === '') {
        return symbols.map((items) => ({
          ...items,
          symbols: items.symbols,
        }));
      }

      const mappedSymbols = symbols.map((items) => ({
        ...items,
        symbols: matchSorter([...items.symbols], debouncedSearchTerm, {
          keys: ['name'],
          threshold: matchSorter.rankings.CONTAINS,
        }),
      }));

      return mappedSymbols;
    });
  }, [debouncedSearchTerm, symbols]);

  useEffect(() => {
    const foundItems = currentSymbols.map((category) => category.symbols).flat();

    setSearchFound(searchTerm === '' ? null : foundItems.length);
  }, [currentSymbols, searchTerm]);

  return useMemo(
    () => ({
      showModal,
      searchFound,
      currentSymbols,
      onChooseSymbol,
      onChangeCategory,
      onChangeSearchText,
      onToggleFilterModal,
      debouncedSearchTerm,
      onSearchInAllCategories,
    }),
    [
      showModal,
      searchFound,
      currentSymbols,
      onChooseSymbol,
      onChangeCategory,
      onChangeSearchText,
      onToggleFilterModal,
      debouncedSearchTerm,
      onSearchInAllCategories,
    ],
  );
}

export default useApp;
